import { useState, useCallback, useRef, useEffect } from 'react';
import { apiClient } from '../api/client';
import { matchFaq } from '../chatbot/offline-matcher';
import { matchPersonalization } from '../chatbot/personalization-matcher';
import { matchKrioPhrase } from '../chatbot/krio-phrases';
import { getCachedKnowledgeBase } from '../chatbot/kb-cache';
import { HistoryEntry } from '../chatbot/types';

export interface ChatMessage {
  id: string;
  text: string;
  sender: 'user' | 'bot';
  timestamp: Date;
  source?: 'database' | 'faq' | 'llm' | 'fallback';
  offline?: boolean;
}

interface ChatResponseDto {
  answer: string;
  source: 'database' | 'faq' | 'llm' | 'fallback';
  confidence: 'high' | 'medium' | 'low';
  timestamp: string;
}

const MAX_HISTORY_ENTRIES = 6; // 3 exchange pairs

export function useOfflineChat() {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [loading, setLoading] = useState(false);
  const historyRef = useRef<HistoryEntry[]>([]);

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const getHistoryWindow = useCallback((): HistoryEntry[] => {
    return historyRef.current.slice(-MAX_HISTORY_ENTRIES);
  }, []);

  const sendMessage = useCallback(async (question: string) => {
    const userMsg: ChatMessage = {
      id: `user-${Date.now()}`,
      text: question,
      sender: 'user',
      timestamp: new Date(),
    };
    setMessages((prev) => [...prev, userMsg]);
    historyRef.current.push({ role: 'user', content: question });

    setLoading(true);

    let botMsg: ChatMessage;

    // Check Krio phrases first (instant, no API call)
    const krioResponse = matchKrioPhrase(question);
    if (krioResponse) {
      botMsg = {
        id: `bot-${Date.now()}`,
        text: krioResponse,
        sender: 'bot',
        timestamp: new Date(),
        source: 'faq',
      };
    } else if (!navigator.onLine) {
      botMsg = handleOfflineMessage(question);
    } else {
      botMsg = await handleOnlineMessage(question, getHistoryWindow());
    }

    setMessages((prev) => [...prev, botMsg]);
    historyRef.current.push({ role: 'assistant', content: botMsg.text });
    setLoading(false);
  }, [getHistoryWindow]);

  return { messages, loading, isOnline, sendMessage };
}

function handleOfflineMessage(question: string): ChatMessage {
  const cached = getCachedKnowledgeBase();

  if (!cached) {
    return {
      id: `bot-${Date.now()}`,
      text: 'No cached data available. Please connect to the internet.',
      sender: 'bot',
      timestamp: new Date(),
      source: 'fallback',
      offline: true,
    };
  }

  // Try personalization first
  const personalizationResult = matchPersonalization(question, cached.personalization);
  if (personalizationResult) {
    return {
      id: `bot-${Date.now()}`,
      text: personalizationResult,
      sender: 'bot',
      timestamp: new Date(),
      source: 'faq',
      offline: true,
    };
  }

  // Try FAQ matching
  const faqResult = matchFaq(question, cached.faqEntries);
  if (faqResult) {
    return {
      id: `bot-${Date.now()}`,
      text: faqResult.answer,
      sender: 'bot',
      timestamp: new Date(),
      source: 'faq',
      offline: true,
    };
  }

  // Fallback
  return {
    id: `bot-${Date.now()}`,
    text: "I can't answer that offline. Try again when you're back online.",
    sender: 'bot',
    timestamp: new Date(),
    source: 'fallback',
    offline: true,
  };
}

async function handleOnlineMessage(question: string, history: HistoryEntry[]): Promise<ChatMessage> {
  try {
    const response = await apiClient<ChatResponseDto>('/chatbot/ask', {
      method: 'POST',
      body: { question, history: history.length > 0 ? history : undefined },
    });

    return {
      id: `bot-${Date.now()}`,
      text: response.answer,
      sender: 'bot',
      timestamp: new Date(response.timestamp),
      source: response.source,
    };
  } catch {
    return {
      id: `bot-error-${Date.now()}`,
      text: 'Sorry, something went wrong. Please try again.',
      sender: 'bot',
      timestamp: new Date(),
      source: 'fallback',
    };
  }
}
